import React from 'react';
import * as Yup from "yup";
import {
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    Grid,
    MenuItem,
    Typography
} from '@mui/material';
import {FullWidthText} from '../formFields';
import {FastField, Field, getIn, useFormikContext} from "formik";
import {DatePickerField} from "../formFields/DateFields";
import {Select} from "formik-mui";
import {ManagedSwitch} from "../formFields/ManagedSwitch";

const initial = "initial";
const required = "Required";
const now = new Date();
const yesterday = new Date(now - 86400000);

const yesterdayOrEarlier = (value) => {
    const valueAsDate = new Date(value);
    if (valueAsDate.toDateString() !== "Invalid Date") {
        if (valueAsDate.getTime() > (new Date(yesterday)).getTime()) {
            return "Date cannot be today or in the future";
        }
    } else {
        return "Must be a valid date"
    }
}

const LastInspection = ({...props}) => {
    const lastInspectionName = "dateOfLastInspection";
    const {values, setFieldValue} = useFormikContext();
    const {dependency} = props;
    const dependentValue = getIn(values, dependency);
    const field = (<>
            <Grid item xs={12} sm={6}>
                <DatePickerField
                    name={lastInspectionName}
                    label={"Date of Last Inspection"}
                    disableFuture={true}
                    validate={(value) => {
                        if (dependentValue !== initial) {
                            return yesterdayOrEarlier(value);
                        }
                        return "";
                    }}
                    fullWidth/>
            </Grid>
        </>
    );
    const hidden = (
        <>
            <FastField
                name={lastInspectionName}
                label={"Date of Last Inspection"}
                type={"hidden"}
                value={""}
            />
        </>);

    React.useEffect(() => {
        if (dependentValue === initial) {
            setFieldValue(lastInspectionName, null);
        }
    }, [dependentValue, lastInspectionName, setFieldValue]);

    return dependentValue !== initial ? field : hidden;
};

const FormStatus = () => {
    const formComplete = "formStatus.complete";
    const formComment = "formStatus.comment";
    const {values} = useFormikContext();
    const isComplete = getIn(values, formComplete);

    const common = (<>
        <Grid item xs={12} sm={12}>
            <Typography variant={"subtitle1"}>
                {"Was the inspection completed?"}
            </Typography>
        </Grid>
        <Grid item xs={3} sm={3}>
            <FormControlLabel control={<ManagedSwitch name={formComplete}/>}
                              label={"Yes"}
            />
        </Grid>
    </>);
    const complete = (<>
        {common}
        <FastField
            name={formComment}
            type={"hidden"}
            value={""}
        />
    </>);
    const incomplete = (<>
        {common}
        <Grid item xs={12} sm={12}>
            <FullWidthText
                name={formComment}
                label={"Why couldn't the inspection be completed?"}
                multiline={true}
                spellCheck={true}
                rows={4}
                variant={"outlined"}
            />
        </Grid>
    </>);

    return isComplete ? complete : incomplete;
};

const Basics = () => {
    return (
        <>
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Inspection Basics
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <FullWidthText
                                name={"nameOfFamily"}
                                label={"Name of Family"}
                                fast={true}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FullWidthText
                                name={"tenantIDNumber"}
                                label={"Tenant ID Number"}
                                fast={true}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePickerField
                                disableFuture={true}
                                name={"dateOfRequest"}
                                label={"Date of Request"}
                                validate={yesterdayOrEarlier}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FullWidthText
                                name={"inspector"}
                                label={"Inspector"}
                                fast={true}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FullWidthText
                                name={"neighborhood"}
                                label={"Neighborhood / Census Tract"}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePickerField
                                disableFuture={true}
                                name={"dateOfInspection"}
                                label={"Date of Inspection"}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <Field
                                    component={Select}
                                    name={"typeOfInspection"}
                                    inputProps={{id: "typeOfInspection"}}
                                    defaultValue={initial}
                                    label={"Inspection Type"}
                                    fullWidth
                                >
                                    <MenuItem value={initial}>Initial</MenuItem>
                                    <MenuItem value={"annual"}>Annual</MenuItem>
                                    <MenuItem value={"bi-annual"}>Bi-Annual</MenuItem>
                                    <MenuItem value={"reinspection"}>Reinspection</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>
                        <LastInspection dependency={"typeOfInspection"}/>
                        <Grid item xs={12} sm={6}>
                            <FullWidthText
                                name={"pha"}
                                label={"PHA"}
                                fast={true}
                            />
                        </Grid>
                        <FormStatus/>
                    </Grid>
                </CardContent>
            </Card>
        </>
    );
};

const BasicsDefaults = () => {
    return {
        "nameOfFamily": "",
        "tenantIDNumber": "",
        "dateOfRequest": yesterday,
        "inspector": "",
        "neighborhood": "",
        "dateOfInspection": now,
        "typeOfInspection": "initial",
        "dateOfLastInspection": "",
        "pha": "",
        "formStatus": {
            "complete": false,
            "comment": "",
        },
    };
};
const BasicsValidation = () => {
    return Yup.object().shape({
        nameOfFamily: Yup.string().max(64, "Must be 64 characters or less").required(required),
        tenantIDNumber: Yup.string().max(48, "Must be 48 characters or less"),
        inspector: Yup.string().max(64, "Must be 64 characters or less").required(required),
        neighborhood: Yup.string().max(48, "Must be 48 characters or less"),
        dateOfInspection: Yup.date().required(required),
        typeOfInspection: Yup.string().required(required),
        pha: Yup.string().max(36, "Must be 36 characters or less").required(required),
        formStatus: Yup.object().shape({
            complete: Yup.boolean().required(required),
            comment: Yup.string().max(256, "Comments may not be longer than 256 characters")
        }),
    });
};

export default Basics;
export {BasicsDefaults, BasicsValidation};